/**
 * Creating the family's FIRST quest, for AD-04's "one-quest setup" clause (#576 o6).
 *
 * The endpoint is NOT new. `POST /api/admin/chores` (`server/routes/chore-admin.routes.js:109`) is
 * the same one the production app already calls, behind the `requireAuth` + `requireAdmin` pair the
 * whole `/api/admin` router carries (lines 53-54). Nothing on the server changes for this; only the
 * redesign gains a way in. CR-02 denies DISPLAY sessions there, and a parent in onboarding is not
 * one, so that guard is not in the way either.
 *
 * **Why the child's idempotency cannot be reused, and what stands in for it.** `familyMembers.js`
 * leans on a natural key: `users` is unique on `tenant_id` + `LOWER(username)`, so a replayed write
 * is answered with **409** and a second child cannot be created. Chores carry no such key.
 * `createChoreSchema` requires only a name, a period, an assignment type and a start time; the table
 * has no `last_mutation_id` column, unlike `tenant_quest_packs`; and the route accepts no
 * idempotency key. A second identical submit WOULD create a second quest.
 *
 * So the replay check reads the account's own list first and refuses to write the SAME quest a
 * second time — the same name AND the same period. Name alone is not identity: matching on it made
 * this module impose the very product rule the note below says has not been decided.
 *
 * **THE LIMIT, stated here because a caller cannot infer it.** This is a read-then-write. It closes
 * every resume case the criterion names — a refresh, a retry, a back button, a second visit — and it
 * does NOT make two simultaneous submits safe. Closing that needs a server-side key, and that would
 * impose a product rule nobody has stated: whether one family may keep two quests with the same
 * name. A family can see and delete a duplicate; they cannot undo a rule imposed on their data.
 *
 * **It fails OPEN on a failed read.** If the list request blips, the quest is written anyway.
 * Failing closed would mean a family whose read failed could never add a quest at all, and the cost
 * of failing open is one visible, deletable duplicate.
 */
import { api } from './client.js';

/**
 * The chore admin path, as ONE constant rather than three string literals.
 *
 * **It carries the `/api` prefix, and the first version of this module did not.** Every path in
 * this directory is the full server path — `familyMembers.js` sends `/api/admin/users`, because
 * `client.js` prepends only the ORIGIN (`VITE_API_ORIGIN`) and adds no prefix of its own. A path of
 * `/admin/chores` therefore reached the static server, which answered **405** on the write and,
 * worse, **200** on the read: the SPA's own `index.html`, carrying no `chores` array. So the replay
 * check silently found nothing on every call.
 *
 * Measured on the dev app at `62a7617`, by driving the real form. No test caught it, because the
 * tests asserted the path this module CHOSE rather than the path the server serves — the code and
 * its test were written from the same wrong assumption. The test now asserts the `/api/` prefix,
 * which is the rule, instead of a literal that can be wrong in both places at once.
 */
export const CHORES_PATH = '/api/admin/chores';

/** What any fault creating a quest is called, so no caller reads a status code. */
export const QUEST_FAULT = 'QUEST_FAULT';

/**
 * What an ACCEPTED write with no usable row back is called.
 *
 * It is its own outcome because neither "saved" nor "failed" is known to be true: the server
 * answered without an error, so the quest may well exist, and it returned nothing this code can
 * show. The first version of this module reported it as a plain success, which cleared the draft
 * and told the family their quest was on the list using the name THEY had typed — a claim about a
 * row nobody had seen. Filed by the cross-model review of this change.
 *
 * `familyMembers.js` already carries the same shape for the same reason (`ADD_CHILD_UNCONFIRMED`).
 */
export const QUEST_UNCONFIRMED = 'QUEST_UNCONFIRMED';

/**
 * Who a first quest belongs to.
 *
 * `no-assignment` is the honest default for onboarding: a family reaches this step before any child
 * has to exist, and naming an assignee the family never chose would put a child's name on a quest
 * they never agreed to. `createChoreSchema` accepts exactly
 * `no-assignment | in-alphabetical-order | random`.
 */
export const FIRST_QUEST_ASSIGNMENT = 'no-assignment';

/** The hour a first quest starts on. Morning, because that is when a household's day starts. */
const START_HOUR = 9;

/** Normalize a quest name for COMPARISON only. What gets written is what the family typed. */
function compareKey(name) {
  return typeof name === 'string' ? name.trim().toLowerCase().replace(/\s+/g, ' ') : null;
}

/**
 * Find the quest that is the SAME quest: same name, ignoring case and repeated spaces, AND the
 * same period.
 *
 * **The period is half the identity on purpose, and the first version of this module got it
 * wrong.** Matching on the name alone made the module impose the very product rule its own note
 * said had not been decided: a family who wants "Tidy up" every day AND "Tidy up" every week was
 * told to pick another name. Filed by the cross-model review of this change.
 *
 * Name matching stays forgiving, because a family retyping "Feed the cat" after a refresh will not
 * reproduce their own capitals, and a name differing only by a double space is the same quest to
 * them. The period is not forgiving: it is a choice from two options, so a difference there is a
 * difference the family made.
 *
 * @param {Array} quests rows from `GET /api/admin/chores`
 * @param {string} name the name being written
 * @param {string} [periodType] the period being written; omit to match on the name alone
 * @returns {object|null} the existing row, or `null`. Never throws.
 */
export function findQuestByName(quests, name, periodType) {
  const wanted = compareKey(name);
  if (!Array.isArray(quests) || wanted === null) return null;
  return (
    quests.find(
      (quest) =>
        quest &&
        typeof quest === 'object' &&
        compareKey(quest.name) === wanted &&
        (periodType === undefined || quest.periodType === periodType),
    ) || null
  );
}

/**
 * When the first quest should first come due.
 *
 * TOMORROW morning, never today. "Today at 9" is already in the past for anyone setting up after
 * breakfast, and a quest that starts in the past is the one value a family would read as broken.
 *
 * @param {Date} [now] the clock, injectable so the rule can be tested rather than trusted
 * @returns {string} an ISO timestamp. Never throws, even on an unusable clock.
 */
export function firstExecutionTime(now = new Date()) {
  const base = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
  const start = new Date(base.getTime());
  start.setDate(start.getDate() + 1);
  start.setHours(START_HOUR, 0, 0, 0);
  return start.toISOString();
}

/** A reward the server accepts: `z.number().min(0)`. */
const isAmount = (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0;

/** Money is whole cents. A stepper in quarters never needs this, but a caller is not a stepper. */
const toCents = (value) => Math.round(value * 100) / 100;

/**
 * Change ONE quest's reward — the "editable reward review" of onboarding (#577 row 3).
 *
 * The endpoint is the one Quest Studio edits through: `PUT /api/admin/chores/:id`, whose schema is
 * `createChoreSchema.partial()`, so a body of `{ reward }` changes the reward and nothing else. It is
 * a parent change, so the server asks for the #578 grown-up check, and the shared client shows it.
 *
 * The reply must name the SAME quest carrying the SAME amount. Anything else is `QUEST_UNCONFIRMED`:
 * the write may have landed, so the caller must not say "nothing was saved".
 *
 * @param {number} id the quest
 * @param {number} reward the new amount, in dollars
 * @param {{put: Function}} [seam] the API surface, injectable for tests
 * @returns {Promise<object>} the SERVER's row
 * @throws a plain Error for an amount the server would refuse (nothing is sent); `ApiError` for a
 *         refused write; a `QUEST_UNCONFIRMED` error for an accepted write that did not confirm.
 */
export async function updateFamilyQuestReward(id, reward, seam = api) {
  if (!Number.isInteger(id) || id <= 0) throw new Error('that quest has no id');
  if (!isAmount(reward)) throw new Error('a reward is a number of dollars, zero or more');
  const amount = toCents(reward);
  const saved = await seam.put(`${CHORES_PATH}/${encodeURIComponent(id)}`, { reward: amount });
  const row = saved && saved.success === true ? saved.chore : null;
  const raw = row?.reward;
  const echoed = typeof raw === 'number' || (typeof raw === 'string' && /^\d+(?:\.\d+)?$/.test(raw))
    ? Number(raw) : Number.NaN;
  if (!row || row.id !== id || Math.round(echoed * 100) !== Math.round(amount * 100)) {
    const unconfirmed = new Error('the reward change was accepted but not confirmed');
    unconfirmed.questCode = QUEST_UNCONFIRMED;
    throw unconfirmed;
  }
  return row;
}

/**
 * Read the family's quest list.
 *
 * @param {{get: Function}} [seam] the API surface, injectable for tests
 * @returns {Promise<Array>} the rows, or an empty array when the body carries none
 */
export async function fetchFamilyQuests(seam = api) {
  const listed = await seam.get(CHORES_PATH);
  return Array.isArray(listed?.chores) ? listed.chores : [];
}

/**
 * Name a failure creating a quest, so a caller never reads a status code.
 *
 * @returns {string} `QUEST_UNCONFIRMED` when the write was accepted but returned nothing this code
 *          could show, and `QUEST_FAULT` for everything else. The two need DIFFERENT sentences: one
 *          says nothing was saved, and the other must not.
 */
export function questErrorCode(error) {
  return error && error.questCode === QUEST_UNCONFIRMED ? QUEST_UNCONFIRMED : QUEST_FAULT;
}

/**
 * Add ONE quest to the family's account, writing nothing when the name is already there.
 *
 * @param {{name: string, periodType: string}} draft what the family typed
 * @param {{get: Function, post: Function}} [seam] the API surface, injectable for tests
 * @returns {Promise<{quest: object, replayed: boolean}>} the SERVER's row, and whether it already
 *          existed. `replayed: true` means nothing was written.
 * @throws when the write itself fails. A failed READ never throws — see the module note.
 */
export async function createFamilyQuest(draft, seam = api) {
  const name = typeof draft?.name === 'string' ? draft.name.trim() : '';
  const periodType = draft?.periodType === 'weekly' ? 'weekly' : 'daily';

  // The replay check. Its failure is swallowed on purpose: see the module note on failing open.
  let existing = null;
  try {
    const listed = await seam.get(CHORES_PATH);
    existing = findQuestByName(listed?.chores, name, periodType);
  } catch {
    /* The list could not be read. Write anyway rather than trap the family. */
  }
  if (existing !== null) return { quest: existing, replayed: true };

  // The write itself is NOT wrapped: a failure here must reach the caller, because it is the one
  // thing that means the quest does not exist.
  const created = await seam.post(CHORES_PATH, {
    name,
    periodType,
    assignmentType: FIRST_QUEST_ASSIGNMENT,
    nextExecutionTime: firstExecutionTime(),
    // #577 row 3. Only a real amount; with none the column's own default decides.
    ...(isAmount(draft?.reward) ? { reward: toCents(draft.reward) } : {}),
  });
  // The SERVER's row, never the draft that was sent — a name it trimmed or changed must not be
  // shown back as though the family had typed it.
  const row = created?.chore;
  if (!row || typeof row !== 'object' || typeof row.name !== 'string') {
    // Accepted, but nothing to show. Say so rather than claim a save: see QUEST_UNCONFIRMED.
    const unconfirmed = new Error('the quest write was accepted but returned no row');
    unconfirmed.questCode = QUEST_UNCONFIRMED;
    throw unconfirmed;
  }
  return { quest: row, replayed: false };
}
